import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, LogOut, Smartphone, Bell, Heart, UserPlus, ShieldCheck, Trash2, Users,
         Share2, Plus, Check, Loader2 } from "lucide-react";
import { useAuth } from "../AuthContext";
import { PwaInstall } from "../components/PwaInstall";
import { InviteFriends } from "../components/InviteFriends";
import api from "../api";
import { toast } from "sonner";

const HOBBIES = ["Reading", "Drawing", "Music", "Lego", "Gaming", "Dance", "Cooking", "Coding"];
const SPORTS = ["Cricket", "Football", "Basketball", "Swimming", "Tennis", "Skating", "Badminton"];
const IMPROVEMENT_AREAS = [
  { key: "confidence",              en: "Confidence",            hi: "आत्मविश्वास" },
  { key: "discipline",              en: "Discipline",            hi: "अनुशासन" },
  { key: "hard_work",               en: "Hard Work",             hi: "मेहनत" },
  { key: "following_instructions",  en: "Following Instructions", hi: "निर्देशों का पालन" },
  { key: "selflessness",            en: "Selflessness",          hi: "निस्वार्थता" },
  { key: "focus",                   en: "Focus",                 hi: "एकाग्रता" },
  { key: "screen_habits",           en: "Screen Habits",         hi: "स्क्रीन की आदतें" },
];
const IMPROVEMENT_LIMIT = 2;

function AddChildModal({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("4-8");
  const [hobbies, setHobbies] = useState([]);
  const [sport, setSport] = useState("");
  const [improvementAreas, setImprovementAreas] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleHobby = (h) =>
    setHobbies((arr) => (arr.includes(h) ? arr.filter((x) => x !== h) : [...arr, h]));

  const toggleImprovement = (key) => {
    setImprovementAreas((arr) => {
      if (arr.includes(key)) return arr.filter((x) => x !== key);
      if (arr.length >= IMPROVEMENT_LIMIT) {
        toast.message(`Pick up to ${IMPROVEMENT_LIMIT} focus areas`);
        return arr;
      }
      return [...arr, key];
    });
  };

  if (!open) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/profile/child", { name, age_group: ageGroup,
                                          hobbies,
                                          favorite_sport: sport || null,
                                          favorite_activities: [],
                                          improvement_areas: improvementAreas });
      toast.success("Child added");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  const chipClass = (selected) =>
    `px-3 py-1 rounded-full border text-xs font-medium transition-all ${
      selected
        ? "bg-[hsl(var(--neu-orange))] text-white border-[hsl(var(--neu-orange))]"
        : "bg-white/70 text-[hsl(var(--neu-ink))] hover:bg-white"
    }`;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 fade-in"
         data-testid="add-child-modal" onClick={onClose}>
      <form onSubmit={save} className="card-elevated w-full max-w-md p-6 rise-in" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-2xl mb-1">Add another child</h3>
        <p className="text-sm text-muted-foreground mb-5">Each child gets their own personalized stories.</p>
        <input required value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Child's name" className="field mb-4" autoFocus data-testid="add-child-name"/>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {["4-8", "9-13"].map((g) => (
            <button type="button" key={g} onClick={() => setAgeGroup(g)}
                    className={`p-3 rounded-2xl border ${ageGroup === g ? "bg-[hsl(var(--neu-orange))]/10 border-[hsl(var(--neu-orange))]" : "bg-white/70"}`}
                    data-testid={`add-child-age-${g}`}>
              <div className="font-display text-xl">{g}</div>
              <div className="text-xs text-muted-foreground">years</div>
            </button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mb-2">Hobbies (pick any number, optional)</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {HOBBIES.map((h) => (
            <button type="button" key={h} onClick={() => toggleHobby(h)}
                    className={chipClass(hobbies.includes(h))}
                    data-testid={`add-child-hobby-${h.toLowerCase()}`}>{h}</button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mb-2">Favorite sport (optional)</div>
        <div className="flex flex-wrap gap-2 mb-5">
          {SPORTS.map((s) => (
            <button type="button" key={s} onClick={() => setSport(sport === s ? "" : s)}
                    className={chipClass(sport === s)}
                    data-testid={`add-child-sport-${s.toLowerCase()}`}>{s}</button>
          ))}
        </div>
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-xs text-muted-foreground">
            Areas of improvement (pick up to {IMPROVEMENT_LIMIT})
          </div>
          <div className="text-[10px] text-muted-foreground">
            {improvementAreas.length}/{IMPROVEMENT_LIMIT}
          </div>
        </div>
        <div className="rounded-xl bg-[hsl(var(--neu-orange))]/8 border border-[hsl(var(--neu-orange))]/25 px-3 py-2 mb-3 text-[11px] text-[hsl(var(--neu-ink))]">
          <span className="font-medium text-[hsl(var(--neu-orange))]">Note · </span>
          These two focus areas will guide your child&apos;s conversations for the next one-year plan.
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {IMPROVEMENT_AREAS.map((a) => {
            const on = improvementAreas.includes(a.key);
            const disabled = !on && improvementAreas.length >= IMPROVEMENT_LIMIT;
            return (
              <button type="button" key={a.key}
                      onClick={() => toggleImprovement(a.key)}
                      disabled={disabled}
                      className={`px-3 py-1 rounded-full border text-xs font-medium transition-all ${
                        on
                          ? "bg-[hsl(var(--neu-orange))] text-white border-[hsl(var(--neu-orange))]"
                          : disabled
                            ? "bg-white/40 text-muted-foreground opacity-50 cursor-not-allowed"
                            : "bg-white/70 text-[hsl(var(--neu-ink))] hover:bg-white"
                      }`}
                      data-testid={`add-child-improvement-${a.key}`}>
                {a.en}
                <span className={`ml-1 ${on ? "text-white/85" : "text-muted-foreground"}`}>
                  {a.hi}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button disabled={busy} className="btn-primary flex-1" data-testid="add-child-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Add child"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Settings() {
  const nav = useNavigate();
  const { me, logout, refresh } = useAuth();
  const [installOpen, setInstallOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [time, setTime] = useState(me?.profile?.notification_pref?.time_local || "18:00");
  const [busy, setBusy] = useState(false);
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [delConfirm, setDelConfirm] = useState(false);

  const loadKids = async () => {
    try {
      const r = await api.get("/profile/children");
      setChildren(r.data.children);
      setActiveChildId(r.data.active_child_id);
    } catch {}
  };
  useEffect(() => { loadKids(); }, []);

  const saveTime = async () => {
    setBusy(true);
    try {
      await api.patch("/profile/notification", { time_local: time, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      await refresh();
      toast.success("Notification time saved");
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const setActive = async (id) => {
    try {
      await api.post(`/profile/active-child/${id}`);
      setActiveChildId(id);
      await refresh();
      toast.success("Active child changed");
    } catch { toast.error("Could not change"); }
  };

  const removeChild = async (id) => {
    if (!window.confirm("Remove this child profile? Their sessions stay in your history.")) return;
    try {
      await api.delete(`/profile/child/${id}`);
      toast.success("Child removed");
      loadKids();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/profile/account");
      localStorage.removeItem("neuroeq_token");
      toast.success("Account deleted. We're sad to see you go.");
      window.location.href = "/";
    } catch { toast.error("Could not delete account"); }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="settings-screen">
      <div className="max-w-md mx-auto px-6 pt-8">
        <div className="flex items-center gap-3 mb-7">
          <button onClick={() => nav(-1)} className="p-2 rounded-full hover:bg-white/70" data-testid="settings-back">
            <ArrowLeft className="w-5 h-5"/>
          </button>
          <h1 className="font-display text-3xl">Settings</h1>
        </div>

        {/* Parent */}
        <div className="card-soft p-5 mb-4" data-testid="settings-parent-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Parent</div>
          <div className="font-display text-2xl mt-1">{me?.profile?.name}</div>
          <div className="text-sm text-muted-foreground">{me?.user?.email}</div>
        </div>

        {/* Children */}
        <div className="card-soft p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[hsl(var(--neu-orange))]"/>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Children</div>
            </div>
            <button onClick={() => setAddChildOpen(true)} className="btn-ghost px-3 py-1.5 text-sm"
                    data-testid="settings-add-child-btn">
              <Plus className="w-3 h-3"/> Add child
            </button>
          </div>
          <div className="space-y-2">
            {children.map((c) => (
              <div key={c.id} className="flex items-center gap-3" data-testid={`settings-child-${c.id}`}>
                <button onClick={() => setActive(c.id)}
                        className={`flex-1 text-left p-3 rounded-2xl border transition-colors ${
                          activeChildId === c.id ? "bg-[hsl(var(--neu-orange))]/10 border-[hsl(var(--neu-orange))]" : "bg-white/70 hover:bg-white"
                        }`}>
                  <div className="font-display text-lg flex items-center gap-2">
                    {c.name}
                    {activeChildId === c.id && <Check className="w-4 h-4 text-[hsl(var(--neu-orange))]"/>}
                  </div>
                  <div className="text-xs text-muted-foreground">Age {c.age_group}</div>
                </button>
                {children.length > 1 && (
                  <button onClick={() => removeChild(c.id)}
                          className="p-2 text-muted-foreground hover:text-destructive rounded-full"
                          data-testid={`settings-remove-child-${c.id}`}>
                    <Trash2 className="w-4 h-4"/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Reminder */}
        <div className="card-soft p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-[hsl(var(--neu-orange))]"/>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sunday reminder</div>
          </div>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="field mb-3" data-testid="settings-time"/>
          <button onClick={saveTime} disabled={busy} className="btn-primary w-full"
                  data-testid="settings-save-time-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save"}
          </button>
        </div>

        {/* Invite, Install, Privacy */}
        <button onClick={() => setInviteOpen(true)} className="btn-ghost w-full mb-3 justify-start"
                data-testid="settings-invite-btn">
          <UserPlus className="w-4 h-4"/> Invite a friend
        </button>

        <button onClick={() => setInstallOpen(true)} className="btn-ghost w-full mb-3 justify-start"
                data-testid="settings-install-btn">
          <Smartphone className="w-4 h-4"/> Install NeuroEQ on this device
        </button>

        <Link to="/privacy" className="btn-ghost w-full mb-3 justify-start"
              data-testid="settings-privacy-link">
          <ShieldCheck className="w-4 h-4"/> Privacy &amp; data
        </Link>

        <button onClick={logout} className="btn-ghost w-full mb-3 justify-start"
                data-testid="settings-logout-btn">
          <LogOut className="w-4 h-4"/> Sign out
        </button>

        {/* Danger zone */}
        <div className="card-soft p-5 mt-7 border-destructive/20 bg-destructive/[0.02]"
             data-testid="settings-danger-zone">
          <div className="text-xs uppercase tracking-widest text-destructive">Danger zone</div>
          <p className="text-sm text-muted-foreground mt-2 mb-4">
            Delete your account and everything we store about your family. reflections, wishes, streaks, badges.
            This cannot be undone.
          </p>
          {!delConfirm ? (
            <button onClick={() => setDelConfirm(true)} className="btn-ghost w-full text-destructive border-destructive/30"
                    data-testid="settings-delete-btn">
              <Trash2 className="w-4 h-4"/> Delete account
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-destructive">Are you sure? This is permanent.</p>
              <div className="flex gap-2">
                <button onClick={() => setDelConfirm(false)} className="btn-ghost flex-1">Cancel</button>
                <button onClick={deleteAccount} className="btn-primary flex-1 !bg-destructive hover:!bg-destructive/90"
                        data-testid="settings-delete-confirm">
                  Yes, delete everything
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
          Made with <Heart className="w-3 h-3 text-[hsl(var(--neu-pink))]"/> for families
        </div>
      </div>

      {installOpen && <PwaInstall forceOpen onClose={() => setInstallOpen(false)} />}
      <InviteFriends open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <AddChildModal open={addChildOpen} onClose={() => setAddChildOpen(false)}
                      onSaved={() => { setAddChildOpen(false); loadKids(); }} />
    </div>
  );
}
